/**
 * useGMBStats Hook
 *
 * Provides aggregated statistics for all GMB locations including:
 * - Total reviews across all locations
 * - Average rating (weighted by review count)
 * - Pending responses count
 * - Response rate percentage
 * - Recent activity metrics
 *
 * Created: 2025-06-28
 * Fixes hardcoded stats in GMBManagement.tsx
 */

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export interface GMBStats {
  totalReviews: number
  averageRating: number
  pendingResponses: number
  responseRate: number
  totalLocations: number
  reviewsThisMonth: number
  ratingChange: number
  isLoading: boolean
  error: Error | null
}

export interface GMBLocationStats {
  location_name: string
  gmb_location_id: string
  total_reviews: number
  average_rating: number
  unanswered_questions: number
  pending_responses: number
}

/**
 * Fetch aggregated GMB statistics across all locations
 */
export function useGMBStats() {
  return useQuery({
    queryKey: ['gmb-stats-aggregate'],
    queryFn: async (): Promise<GMBStats> => {
      // Get all active GMB locations with their metrics
      const { data: locations, error: locationsError } = await supabase
        .from('gmb_locations')
        .select('gmb_location_id, location_name, total_reviews, average_rating, unanswered_questions')
        .eq('is_active', true)

      if (locationsError) throw locationsError

      const locationList = locations || []
      const totalLocations = locationList.length

      // Calculate total reviews and weighted average rating
      let totalReviews = 0
      let weightedRatingSum = 0

      for (const loc of locationList) {
        const reviews = loc.total_reviews || 0
        const rating = loc.average_rating || 0
        totalReviews += reviews
        weightedRatingSum += reviews * rating
      }

      const averageRating = totalReviews > 0
        ? Math.round((weightedRatingSum / totalReviews) * 10) / 10
        : 0

      // Get count of reviews without replies (pending responses)
      const { count: pendingResponses } = await supabase
        .from('reviews')
        .select('*', { count: 'exact', head: true })
        .eq('source', 'gmb')
        .is('reply_comment', null)

      const pendingCount = pendingResponses || 0

      // Calculate response rate
      const responseRate = totalReviews > 0
        ? Math.round(((totalReviews - pendingCount) / totalReviews) * 100)
        : 0

      // Get reviews from this month
      const startOfMonth = new Date()
      startOfMonth.setDate(1)
      startOfMonth.setHours(0, 0, 0, 0)

      const { count: reviewsThisMonth } = await supabase
        .from('reviews')
        .select('*', { count: 'exact', head: true })
        .eq('source', 'gmb')
        .gte('created_at', startOfMonth.toISOString())

      // Get reviews from last month for comparison
      const startOfLastMonth = new Date(startOfMonth)
      startOfLastMonth.setMonth(startOfLastMonth.getMonth() - 1)

      const { data: lastMonthReviews } = await supabase
        .from('reviews')
        .select('star_rating')
        .eq('source', 'gmb')
        .gte('created_at', startOfLastMonth.toISOString())
        .lt('created_at', startOfMonth.toISOString())

      // Calculate rating change (current month avg - last month avg)
      let ratingChange = 0
      if (lastMonthReviews && lastMonthReviews.length > 0) {
        const lastMonthAvg = lastMonthReviews.reduce((sum, r) => {
          const rating = typeof r.star_rating === 'number' ? r.star_rating : 0
          return sum + rating
        }, 0) / lastMonthReviews.length

        ratingChange = Math.round((averageRating - lastMonthAvg) * 10) / 10
      }

      return {
        totalReviews,
        averageRating,
        pendingResponses: pendingCount,
        responseRate,
        totalLocations,
        reviewsThisMonth: reviewsThisMonth || 0,
        ratingChange,
        isLoading: false,
        error: null
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000 // Refetch every 5 minutes
  })
}

/**
 * Fetch per-location statistics for detailed breakdown
 */
export function useGMBLocationStats() {
  return useQuery({
    queryKey: ['gmb-location-stats'],
    queryFn: async (): Promise<GMBLocationStats[]> => {
      // Get all active GMB locations
      const { data: locations, error: locationsError } = await supabase
        .from('gmb_locations')
        .select('gmb_location_id, location_name, total_reviews, average_rating, unanswered_questions')
        .eq('is_active', true)
        .order('total_reviews', { ascending: false })

      if (locationsError) throw locationsError

      const locationList = locations || []

      // Get pending response counts per location
      const locationIds = locationList.map(l => l.gmb_location_id)

      const { data: pendingByLocation } = await supabase
        .from('reviews')
        .select('gmb_location_id')
        .eq('source', 'gmb')
        .is('reply_comment', null)
        .in('gmb_location_id', locationIds)

      // Count pending responses per location
      const pendingCounts: Record<string, number> = {}
      for (const review of pendingByLocation || []) {
        if (review.gmb_location_id) {
          pendingCounts[review.gmb_location_id] = (pendingCounts[review.gmb_location_id] || 0) + 1
        }
      }

      return locationList.map(loc => ({
        location_name: loc.location_name,
        gmb_location_id: loc.gmb_location_id,
        total_reviews: loc.total_reviews || 0,
        average_rating: loc.average_rating || 0,
        unanswered_questions: loc.unanswered_questions || 0,
        pending_responses: pendingCounts[loc.gmb_location_id] || 0
      }))
    },
    staleTime: 5 * 60 * 1000
  })
}

/**
 * Fetch recent activity for the activity feed
 */
export function useGMBRecentActivity(limit: number = 10) {
  return useQuery({
    queryKey: ['gmb-recent-activity', limit],
    queryFn: async () => {
      // Get recent reviews
      const { data: recentReviews } = await supabase
        .from('reviews')
        .select('id, reviewer_name, star_rating, reviewer_comment, created_at, location_name, reply_comment')
        .eq('source', 'gmb')
        .order('created_at', { ascending: false })
        .limit(limit)

      // Get recent posts
      const { data: recentPosts } = await supabase
        .from('gmb_posts')
        .select('id, gmb_location_id, summary, topic_type, state, created_at')
        .order('created_at', { ascending: false })
        .limit(limit)

      // Get recent questions
      const { data: recentQuestions } = await supabase
        .from('gmb_questions')
        .select('id, gmb_location_id, text, is_answered, created_at')
        .order('created_at', { ascending: false })
        .limit(limit)

      // Combine and sort by date
      const activities = [
        ...(recentReviews || []).map(r => ({
          type: 'review' as const,
          id: r.id,
          title: `${r.star_rating}-star review`,
          description: r.reviewer_name
            ? `${r.reviewer_name} left a review`
            : 'New review received',
          timestamp: r.created_at,
          metadata: {
            rating: r.star_rating,
            hasReply: !!r.reply_comment,
            location: r.location_name,
            comment: r.reviewer_comment?.substring(0, 100)
          }
        })),
        ...(recentPosts || []).map(p => ({
          type: 'post' as const,
          id: p.id,
          title: 'Post published',
          description: p.summary?.substring(0, 50) || 'New post',
          timestamp: p.created_at,
          metadata: {
            topicType: p.topic_type,
            state: p.state
          }
        })),
        ...(recentQuestions || []).map(q => ({
          type: 'question' as const,
          id: q.id,
          title: q.is_answered ? 'Question answered' : 'Question needs answer',
          description: q.text?.substring(0, 50) || 'Customer question',
          timestamp: q.created_at,
          metadata: {
            isAnswered: q.is_answered
          }
        }))
      ]

      // Sort by timestamp descending
      activities.sort((a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      )

      return activities.slice(0, limit)
    },
    staleTime: 2 * 60 * 1000 // 2 minutes for activity feed
  })
}
